// src/components/homepage/BenefitsSection.js
import { Link } from "react-router-dom";
import {
  ShoppingCart,
  CreditCard,
  Globe,
  Shield,
  FileText,
  CheckCircle,
  Check,
  Clock,
  X,
} from "lucide-react";

function BenefitsSection() {
  const lanes = [
    {
      icon: ShoppingCart,
      title: "Shopify & Checkout",
      badge: "€149 flat fee",
      featured: false,
      problems: [
        "Checkout errors and gateway failures",
        "App conflicts causing cart issues",
        "Orders not syncing with fulfilment",
        "Theme updates breaking the storefront",
      ],
    },
    {
      icon: CreditCard,
      title: "Stripe & SaaS Billing",
      badge: "€249 flat fee",
      featured: true,
      problems: [
        "Stripe webhook failures",
        "API errors and wrong keys in production",
        "Subscription renewal logic broken",
        "3DS / SCA causing card declines",
        "Stripe Checkout not loading",
      ],
    },
    {
      icon: Globe,
      title: "Site & Infrastructure",
      badge: "€149 flat fee",
      featured: false,
      problems: [
        "SSL certificate expired",
        "DNS misconfigured",
        "Site completely offline",
        "Email not sending or receiving",
      ],
    },
  ];

  const guarantees = [
    {
      icon: Shield,
      title: "Fixed or fully refunded",
      text: "If your submitted incident is within scope and our expert cannot resolve it, you pay nothing. No arguments.",
    },
    {
      icon: FileText,
      title: "Formal Resolution Report",
      text: "Every session ends with an INC-YYYY-XXXXXX Incident Resolution Report — root cause, actions taken, 48-hour warranty window.",
    },
    {
      icon: CheckCircle,
      title: "Vetted experts only",
      text: "Every expert passes a 4-stage vetting process including a live mock session under pressure. 1 in 5 applicants pass.",
    },
  ];

  const shieldBenefits = [
    "Priority Fast-Track Queue — guaranteed expert connection within 45 minutes.",
    "1 included session per month (non-rollover)",
    "Discounted additional sessions — £99 vs full price",
    "Quarterly payment health check",
    "Full incident history and all resolution reports",
    "No contracts — cancel anytime",
  ];

  const notFixed = [
    "Stripe account reviews or risk holds",
    "KYC verification delays",
    "Chargeback or refund disputes",
    "Google or Meta ad account bans",
    "Platform-wide outages (Stripe, Shopify, AWS globally down)",
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* SECTION A — What we fix */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              What we fix
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Flat-fee live sessions for the incidents that stop your revenue.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {lanes.map((lane, index) => {
              const Icon = lane.icon;
              return (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl p-8 transition ${
                    lane.featured
                      ? "border-2 border-blue-500 ring-1 ring-blue-100"
                      : "border border-slate-200"
                  }`}
                >
                  {lane.featured && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-blue-600 text-white text-xs font-bold uppercase tracking-wide">
                      Most requested
                    </span>
                  )}

                  <div className="w-14 h-14 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7 text-blue-600" />
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {lane.title}
                  </h3>
                  <span className="inline-block mb-5 px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-sm font-semibold">
                    {lane.badge}
                  </span>

                  <ul className="space-y-3">
                    {lane.problems.map((problem, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600">
                        <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{problem}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* SECTION B — Our guarantee */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our guarantee
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The commitments behind every resolution session.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {guarantees.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-slate-300 transition"
                >
                  <div className="w-14 h-14 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>

          {/* SLA commitment — response-window policy */}
          <div className="mt-8 rounded-2xl border border-blue-200 bg-blue-50 p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <div className="w-14 h-14 flex-shrink-0 bg-blue-600 rounded-xl flex items-center justify-center">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <div>
                <span className="inline-block mb-3 px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-bold uppercase tracking-wide">
                  &lt; 120 min guaranteed
                </span>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  The 2-Hour SLA Commitment
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  If an expert is not on screen with you within 120 minutes of
                  confirmed triage during operating hours (07:00–23:00 CET), your
                  session is automatically discounted by 50% or eligible for a full
                  refund.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION C — Stay protected (Recovery Shield) */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Stay protected
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Ongoing cover for teams that cannot afford to wait.
            </p>
          </div>

          <div className="max-w-2xl mx-auto bg-slate-50 rounded-2xl border border-slate-200 p-8 sm:p-10">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-1.5 rounded-full bg-blue-600 text-white text-sm font-bold mb-4">
                £49/month
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                Recovery Shield
              </h3>
              <p className="text-slate-600">For founders who cannot afford downtime</p>
            </div>

            <ul className="space-y-4 mb-8">
              {shieldBenefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{benefit}</span>
                </li>
              ))}
            </ul>

            <div className="text-center">
              <Link
                to="/submit-problem"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 bg-blue-600 text-white font-bold text-lg rounded-xl hover:bg-blue-700 transition-all shadow-sm hover:shadow-md"
              >
                Get Recovery Shield
              </Link>
            </div>
          </div>
        </div>

        {/* SECTION D — What we don't fix */}
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-8 sm:p-12">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3">
                What we don't fix
              </h2>
              <p className="text-slate-600">
                We are transparent about the limits of a live expert session.
              </p>
            </div>

            <ul className="space-y-3 mb-6">
              {notFixed.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-slate-700 bg-white rounded-lg border border-slate-200 px-4 py-3"
                >
                  <X className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>

            <p className="text-sm text-slate-500 text-center">
              Our triage process catches these before you pay and redirects you to the right resource.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BenefitsSection;
